import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { HeroCanvas } from "../three/HeroScene";
import { Magnetic } from "../ui/Magnetic";
import { SocialIcon } from "../ui/Icons";
import { profile } from "../../data/content";
import { EASE } from "../../lib/motion";

function RotatingRole({ start }: { start: boolean }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!start) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % profile.roles.length), 2400);
    return () => clearInterval(id);
  }, [start]);

  return (
    <div className="flex h-8 items-center justify-center overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.span
          key={index}
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          exit={{ y: "-100%", opacity: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="font-mono text-sm uppercase tracking-[0.35em] text-accent md:text-base"
        >
          {profile.roles[index]}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

function NameReveal({ start }: { start: boolean }) {
  const first = "Sneha".split("");
  const last = "Sudhir".split("");

  return (
    <h1
      className="font-display font-extrabold leading-[0.92] tracking-tight text-white"
      style={{ fontSize: "clamp(3rem, 11vw, 8.5rem)" }}
    >
      <span className="block overflow-hidden pb-[0.06em]">
        {first.map((l, i) => (
          <motion.span
            key={i}
            className="inline-block will-change-transform"
            initial={{ y: "110%", rotate: 10 }}
            animate={start ? { y: "0%", rotate: 0 } : {}}
            transition={{ duration: 0.95, ease: EASE, delay: 0.15 + i * 0.06 }}
          >
            {l}
          </motion.span>
        ))}
      </span>
      <span className="block overflow-hidden pb-[0.08em]">
        {last.map((l, i) => (
          <motion.span
            key={i}
            className="text-gradient-animated inline-block will-change-transform"
            initial={{ y: "110%", rotate: 10 }}
            animate={start ? { y: "0%", rotate: 0 } : {}}
            transition={{ duration: 0.95, ease: EASE, delay: 0.5 + i * 0.06 }}
          >
            {l}
          </motion.span>
        ))}
      </span>
    </h1>
  );
}

export function Hero({ start }: { start: boolean }) {
  return (
    <section
      id="top"
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
    >
      {/* 3D background */}
      <div className="pointer-events-none absolute inset-0">
        <HeroCanvas />
      </div>

      {/* Ambient orbs */}
      <div className="pointer-events-none absolute -left-40 top-1/4 h-[30rem] w-[30rem] rounded-full bg-primary/20 blur-[120px] animate-blob" />
      <div className="pointer-events-none absolute -right-40 bottom-1/4 h-[26rem] w-[26rem] rounded-full bg-accent/15 blur-[120px] animate-blob [animation-delay:-6s]" />

      {/* Bottom fade */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink to-transparent" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-5xl px-6 text-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.05 }}
          className="mb-5 font-mono text-xs uppercase tracking-[0.45em] text-muted md:text-sm"
        >
          — Hello, I&apos;m —
        </motion.p>

        <NameReveal start={start} />

        <motion.div
          initial={{ opacity: 0 }}
          animate={start ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-6"
        >
          <RotatingRole start={start} />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-white/60 md:text-lg"
        >
          {profile.tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 1.3 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <Magnetic strength={0.35}>
            <a
              href="#work"
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-primary to-secondary px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary/25 transition-shadow hover:shadow-primary/40"
              data-cursor
            >
              <span className="relative z-10">View My Work</span>
              <span className="relative z-10 transition-transform duration-300 group-hover:translate-x-1">→</span>
            </a>
          </Magnetic>
          <Magnetic strength={0.35}>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/10"
              data-cursor
            >
              Let&apos;s Talk
            </a>
          </Magnetic>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={start ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-12 flex items-center justify-center gap-3"
        >
          {profile.socials.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noreferrer"
              aria-label={s.label}
              className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/5 text-white/60 transition-all duration-300 hover:-translate-y-1 hover:border-white/30 hover:text-white"
              data-cursor
            >
              <SocialIcon name={s.icon} className="h-4 w-4" />
            </a>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.a
        href="#about"
        initial={{ opacity: 0 }}
        animate={start ? { opacity: 1 } : {}}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-white/40 hover:text-white"
        data-cursor
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <span className="flex h-9 w-5 items-start justify-center rounded-full border border-white/25 p-1">
          <motion.span
            className="h-1.5 w-1.5 rounded-full bg-white"
            animate={{ y: [0, 14, 0], opacity: [1, 0.2, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.a>
    </section>
  );
}
